import get from "lodash.get";
import set from "lodash.set";
import { v4 } from "uuid";

import {
  MessageBoxDeviceConfig,
  MessageBoxWidgetDto,
  ProgressBarWidgetDeviceConfig,
  ProgressBarWidgetDto,
  QuantityButtonsDeviceConfig,
  QuantityButtonsWidgetDto,
  ResolvedMessageBoxDeviceConfig,
  ResolvedProgressBarDeviceConfig,
  ResolvedQuantityButtonsDeviceConfig,
  WidgetDeviceType,
  WidgetDto,
  WidgetType,
} from "@intelligems/ig-types";
import {
  resolveDesktopMessageBoxConfig,
  resolveMobileMessageBoxConfig,
} from "@intelligems/ig-widgets/MessageBoxView/resolveMessageBoxConfig";
import {
  resolveProgressBarDesktopConfig,
  resolveProgressBarMobileConfig,
} from "@intelligems/ig-widgets/ProgressBarView/resolveProgressBarWidgetConfig";
import {
  resolveDesktopQuantityButtonsConfig,
  resolveMobileQuantityButtonsConfig,
} from "@intelligems/ig-widgets/QuantityButtonsView/resolveQuantityButtonsConfig";
import { Widget, WidgetConfigKey } from "@src/store/slices/widgets/types";

export const filterWidgets = <T extends Widget>(
  widgets: Widget[],
  types: {
    discountProgressBar?: true;
    shippingProgressBar?: true;
    messageBoxPopup?: true;
    messageBoxSlideOut?: true;
    quantityButtons?: true;
  }
) => widgets.filter((w) => types[w.widgetType]) as T[];

export function resolveWidgetDeviceConfig(
  widget: Widget,
  device: WidgetDeviceType
) {
  switch (widget.widgetType) {
    case "discountProgressBar":
    case "shippingProgressBar":
      return device === "desktop"
        ? resolveProgressBarDesktopConfig(widget.config)
        : resolveProgressBarMobileConfig(widget.config);
    case "messageBoxPopup":
    case "messageBoxSlideOut":
      return device === "desktop"
        ? resolveDesktopMessageBoxConfig(widget.config)
        : resolveMobileMessageBoxConfig(widget.config);
    case "quantityButtons":
      return device === "desktop"
        ? resolveDesktopQuantityButtonsConfig(widget.config)
        : resolveMobileQuantityButtonsConfig(widget.config);
  }
}

export function setAndValidate(
  widget: Widget,
  device: WidgetDeviceType,
  key: WidgetConfigKey,
  value: unknown
) {
  const otherDevice = device === "desktop" ? "mobile" : "desktop";
  const config = getConfig(widget, device);
  const otherConfig = getConfig(widget, otherDevice);

  let resolved = resolveWidgetDeviceConfig(widget, otherDevice);
  setDefaultIfUndefined(otherConfig, resolved, key);

  set(config, key, value);
  resolved = resolveWidgetDeviceConfig(widget, device);
  validate(widget.widgetType, config, resolved);
}

export function validate(
  widgetType: WidgetType,
  config: Widget["config"]["desktop" | "mobile"],
  resolved: Widget["config"]["desktop" | "mobile"]
) {
  switch (widgetType) {
    case "discountProgressBar":
    case "shippingProgressBar":
      validateProgressBar(
        config as ProgressBarWidgetDeviceConfig,
        resolved as ResolvedProgressBarDeviceConfig
      );
      break;
    case "quantityButtons":
      validateQuantityButtons(
        config as QuantityButtonsDeviceConfig,
        resolved as ResolvedQuantityButtonsDeviceConfig
      );
      break;
    case "messageBoxPopup":
    case "messageBoxSlideOut":
      validateMessageBox(
        config as MessageBoxDeviceConfig,
        resolved as ResolvedMessageBoxDeviceConfig
      );
      break;
  }
}

export function isQuantityButtons(
  widget: WidgetDto | undefined
): widget is QuantityButtonsWidgetDto {
  return widget?.widgetType === "quantityButtons";
}

export function isProgressBar(
  widget: WidgetDto | undefined
): widget is ProgressBarWidgetDto {
  const type = widget?.widgetType;
  return type === "discountProgressBar" || type === "shippingProgressBar";
}

export function isMessageBox(
  widget: WidgetDto | undefined
): widget is MessageBoxWidgetDto {
  const type = widget?.widgetType;
  return type === "messageBoxPopup" || type === "messageBoxSlideOut";
}

function getConfig(widget: Widget, device: WidgetDeviceType) {
  let config = widget.config[device];
  if (config === undefined) {
    config = widget.config[device] = {};
  }
  return config;
}

function validateProgressBar(
  config: ProgressBarWidgetDeviceConfig,
  resolved: ResolvedProgressBarDeviceConfig
) {
  const { bar, breakpoint } = resolved.styles;
  if (bar.thickness > breakpoint.size) {
    set(config, "styles.bar.thickness", breakpoint.size);
    bar.thickness = breakpoint.size;
  }
  if (bar.cornerRadius > bar.thickness) {
    set(config, "styles.bar.cornerRadius", bar.thickness);
    bar.cornerRadius = bar.thickness;
  }
}

function validateQuantityButtons(
  _config: QuantityButtonsDeviceConfig,
  _resolved: ResolvedQuantityButtonsDeviceConfig
) {}

function validateMessageBox(
  _config: MessageBoxDeviceConfig,
  _resolved: ResolvedMessageBoxDeviceConfig
) {}

export function initializeWidget(widgetType: WidgetType) {
  const widget: Widget = {
    id: v4(),
    name: "",
    enabledSitewide: false,
    widgetType,
    config: {
      desktop: {},
      mobile: {},
    },
    stats: {
      experiences: [],
    },
  };
  switch (widgetType) {
    case "quantityButtons":
      widget.name = "New Button";
      set(widget, "config.desktop.variant", "quantityButtonsWidgetClassic");
      set(widget, "config.mobile.variant", "quantityButtonsWidgetClassic");
      break;
    case "discountProgressBar":
      widget.name = "New Offer Progress Bar";
      set(widget, "config.desktop.variant", "progressBar1");
      set(widget, "config.mobile.variant", "progressBar1");
      break;
    case "messageBoxPopup":
      widget.name = "New Pop Up";
      set(widget, "config.desktop.variant", "messageBoxPopupWidget1");
      set(widget, "config.mobile.variant", "messageBoxPopupWidget1");
      break;
    case "messageBoxSlideOut":
      widget.name = "New Slide Out";
      set(widget, "config.desktop.variant", "messageBoxSlideOutWidget1");
      set(widget, "config.mobile.variant", "messageBoxSlideOutWidget1");
      break;
    case "shippingProgressBar":
      widget.name = "New Shipping Progress Bar";
      set(widget, "config.desktop.variant", "progressBar1");
      set(widget, "config.mobile.variant", "progressBar1");
      break;
  }
  return widget;
}

function setDefaultIfUndefined<T extends object, S extends object>(
  config: T,
  resolved: S | undefined,
  path: string
) {
  const value: unknown = get(config, path);
  if (value === undefined) {
    const defaultValue: unknown = get(resolved, path);
    set(config, path, defaultValue);
  }
}
